/* eslint-disable @typescript-eslint/no-explicit-any */

import './license-list.scss';

import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { AddLicenseModal } from './license-modal';
import { ApolloError } from '@apollo/client';
import {
  DeleteBrokerLicense,
  LicensePaginationSort,
  LicenseSortKey,
  SortOrder,
} from '@converge/graphql';
import { DeleteLicenseModal } from './delete-license-modal';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { ILicenses } from './accordion.broker-form-container';
import { LicenseModal } from '../../../../domain/brokerage';
import { NewDisplayFormatDate } from '@converge/utils';
import { config } from '@converge/config';
import { useConvergeSnackBar } from '../../../../contexts';
import { useState, useEffect, useMemo } from 'react';

export interface ILicenseListProps {
  handleSubmitFromChild: () => void;

  brokerUuid: string | undefined;
  brokerName?: string | undefined;
  brokerLicense?: ILicenses[];
  associateBrokerageUuid: string;
  brokerage: any;
  isLoading?: boolean;
  onSortChange?: (sortModel: LicensePaginationSort) => void;
  currentSort?: LicensePaginationSort;
}

export interface ISelectedDeleteLicense {
  licenseUuid: string | null | undefined;
  brokerUuid: string | null | undefined;
}

const LicenseList = ({
  handleSubmitFromChild,
  brokerUuid,
  brokerLicense,
  brokerage,
  associateBrokerageUuid,
  brokerName,
  isLoading = false,
  onSortChange,
  currentSort,
}: ILicenseListProps) => {
  const renderTableCell = (value: string, testId?: string) => (
    <Typography
      className="table-cell"
      sx={{
        width: '100%',
        paddingLeft: '100px',
      }}
      {...(testId && { 'data-testid': testId })}
    >
      {value || '—'}
    </Typography>
  );

  const renderSkeletonCell = () => (
    <Box
      className="table-cell"
      sx={{
        width: '100%',
        paddingLeft: '20px',
        paddingRight: '20px',
      }}
    >
      <Skeleton animation="wave" width="80%" height={20} />
    </Box>
  );

  const [isBrokerSave, setIsBrokerSave] = useState(false);

  // Local storage utilities for license sort
  const STORAGE_KEY = 'broker-license-sort';

  const getStoredSort = (): LicensePaginationSort => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to parse stored license sort:', error);
    }
    // Default sort by created_at desc
    return {
      key: LicenseSortKey.CreatedAt,
      order: SortOrder.Desc,
    };
  };

  const setStoredSort = (sort: LicensePaginationSort) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sort));
    } catch (error) {
      console.warn('Failed to store license sort:', error);
    }
  };

  // Initialize sort from storage or use current sort
  const [sortModel, setSortModel] = useState<GridSortModel>(() => {
    const sort = currentSort || getStoredSort();

    // Map GraphQL sort to DataGrid sort model
    const fieldMap: Record<LicenseSortKey, string> = {
      [LicenseSortKey.Type]: 'licenseType',
      [LicenseSortKey.Number]: 'licenseNumber',
      [LicenseSortKey.ExpirationDate]: 'expirationDate',
      [LicenseSortKey.CreatedAt]: 'source',
      [LicenseSortKey.Class]: 'licenseType',
      [LicenseSortKey.UpdatedAt]: 'source',
      [LicenseSortKey.HolderFirstName]: '',
      [LicenseSortKey.HolderLastName]: '',
      [LicenseSortKey.Region]: '',
    };

    if (sort.key === LicenseSortKey.CreatedAt) {
      return [];
    }

    const field = sort.key
      ? fieldMap[sort.key] || 'licenseType'
      : 'licenseType';
    const sortOrder = sort.order === SortOrder.Desc ? 'desc' : 'asc';

    return [{ field, sort: sortOrder }];
  });

  // Effect to handle initial sort setup
  useEffect(() => {
    if (!currentSort) {
      const storedSort = getStoredSort();
      onSortChange?.(storedSort);
    }
  }, [currentSort, onSortChange]);
  const [addNewLicense, setAddNewLicense] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<
    ILicenses | null | undefined
  >(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteLicense, setSelectedDeleteLicense] = useState<
    ISelectedDeleteLicense | null | undefined
  >(null);
  const { loading, DeleteLicenseFromPerson } = DeleteBrokerLicense();
  function changeFormat(str: string) {
    return str
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  const { notify } = useConvergeSnackBar();
  const brokerLicenseList = useMemo(() => {
    const mappedList =
      brokerLicense?.map((item) => {
        const fullLicenseHolderName = `${item?.node?.licenseHolder?.firstName}  ${item?.node?.licenseHolder?.lastName}`;
        return {
          id: item.node.uuid,
          licenseHolder: item?.node?.licenseHolder?.firstName
            ? fullLicenseHolderName
            : brokerName,
          licenseType: item.node.licenseType
            ? changeFormat(item.node.licenseType)
            : '—',
          licenseNumber: item.node.licenseNumber ?? '—',
          regionUuid: item.node.region?.name ?? '—',
          expirationDate: item.node?.expirationDate ?? '—',
          source: item.node.source ?? '—',
          isLicenseHolder: Boolean(item?.node?.licenseHolder?.firstName),
        };
      }) || [];

    // Apply client-side sorting
    if (currentSort && mappedList.length > 0) {
      const sortedList = [...mappedList].sort((a, b) => {
        let aValue: string | Date = '';
        let bValue: string | Date = '';

        switch (currentSort.key) {
          case LicenseSortKey.Type:
            aValue = a.licenseType || '';
            bValue = b.licenseType || '';
            break;
          case LicenseSortKey.Number:
            aValue = a.licenseNumber || '';
            bValue = b.licenseNumber || '';
            break;
          case LicenseSortKey.ExpirationDate:
            aValue =
              a.expirationDate === '—'
                ? new Date(0)
                : new Date(a.expirationDate);
            bValue =
              b.expirationDate === '—'
                ? new Date(0)
                : new Date(b.expirationDate);
            break;
          case LicenseSortKey.CreatedAt:
          default:
            aValue = a.source || '';
            bValue = b.source || '';
            break;
        }

        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        return currentSort.order === SortOrder.Desc ? -comparison : comparison;
      });
      return sortedList;
    }

    return mappedList;
  }, [brokerLicense, brokerName, currentSort]);

  const capitalizedFirstLetter = (word: string) =>
    word.charAt(0).toUpperCase() + word.slice(1);

  const noData = [
    {
      id: 0,
      licenseHolderUuid: '—',
      licenseType: '—',
      licenseNumber: '—',
      regionUuid: '—',
      expirationDate: '—',
      source: '—',
    },
  ];

  const loadingData = Array.from({ length: 1 }, (_, index) => ({
    id: `loading-${index}`,
    licenseHolder: 'loading',
    licenseType: 'loading',
    licenseNumber: 'loading',
    regionUuid: 'loading',
    expirationDate: 'loading',
    source: 'loading',
  }));
  const handleDelete = async () => {
    let LicenseResponseErrors;
    try {
      const { errors } = await DeleteLicenseFromPerson({
        variables: {
          person_uuid: selectedDeleteLicense?.brokerUuid,
          license_uuid: selectedDeleteLicense?.licenseUuid,
        },
      });
      LicenseResponseErrors = errors;

      notify(`Broker license has been deleted`, {
        variant: 'success',
      });
      setDeleteModal(false);
    } catch (cause) {
      console.error(`Failed to delete license. Please try again. (${cause})`);
      if (cause instanceof ApolloError) {
        LicenseResponseErrors = cause.graphQLErrors;
      }
    }
    const errorMessage = LicenseResponseErrors?.[0]?.message;
    if (errorMessage) {
      notify(
        `Failed to delete license. Please try again. (${
          errorMessage || 'No data returned by the API'
        })`,
        {
          variant: 'error',
        }
      );
      return;
    }
  };
  const columns: GridColDef[] = [
    {
      field: 'licenseHolder',
      headerName: 'HOLDER',
      flex: 0.4,
      sortable: true,
      renderHeader: () => <span data-testid="holder-header">HOLDER</span>,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell()
          : renderTableCell(
              params.value,
              `holder_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'licenseType',
      headerName: 'TYPE',
      flex: 0.55,
      sortable: true,
      renderHeader: () => <span data-testid="type-header">TYPE</span>,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell()
          : renderTableCell(
              params.value,
              `type_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'regionUuid',
      headerName: 'STATE',
      flex: 0.35,
      sortable: true,
      renderHeader: () => <span data-testid="state-header">STATE</span>,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell()
          : renderTableCell(
              params.value,
              `state_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'licenseNumber',
      headerName: 'NUMBER',
      flex: 0.4,
      sortable: true,
      renderHeader: () => <span data-testid="number-header">NUMBER</span>,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell()
          : renderTableCell(
              params.value,
              `number_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'expirationDate',
      headerName: 'EXPIRATION DATE',
      flex: 0.5,
      sortable: true,
      renderHeader: () => (
        <span data-testid="expiration-date-header">EXPIRATION DATE</span>
      ),
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell()
          : renderTableCell(
              params.value === '—' ? '—' : NewDisplayFormatDate(params.value),
              `expiration-date_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'source',
      headerName: 'SOURCE',
      flex: 0.3,
      sortable: false,
      renderHeader: () => <span data-testid="source-header">SOURCE</span>,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell()
          : renderTableCell(
              capitalizedFirstLetter(params.value),
              `source_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'iconColumn',
      headerName: '',
      flex: 0.9,
      sortable: false,
      renderCell: (params) => {
        const rowIndex =
          params.api.getRowIndexRelativeToVisibleRows(params.id) + 1;
        return (
          <>
            <Typography
              className="table-cell icons"
              aria-label="info"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1px',
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
              }}
            >
              {params.id.toString().startsWith('loading-') ? (
                <Box sx={{ display: 'flex', gap: '10px' }}>
                  <Skeleton animation="wave" width={20} height={20} />
                  <Skeleton animation="wave" width={20} height={20} />
                </Box>
              ) : brokerLicenseList?.length ? (
                <>
                  {!params?.row?.isLicenseHolder ? (
                    <img
                      src={config.editIcon}
                      className="edit-icon"
                      alt="edit-icon"
                      data-testid={`edit_${rowIndex}`}
                      onClick={() => {
                        const selected = brokerLicense?.find(
                          (item) => item.node.uuid === params.id
                        );
                        setSelectedLicense(selected || null);
                        setAddNewLicense(true);
                      }}
                    />
                  ) : (
                    <div className="edit-icon"></div>
                  )}
                  <HighlightOffIcon
                    className="delete-icon"
                    style={{ marginTop: '8px' }}
                    data-testid={`delete_${rowIndex}`}
                    onClick={() => {
                      setDeleteModal(true);
                      setSelectedDeleteLicense({
                        licenseUuid: params.id.toString(),
                        brokerUuid: brokerUuid,
                      });
                    }}
                  />
                </>
              ) : (
                ''
              )}
            </Typography>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Box
        style={{
          height:
            brokerLicenseList && brokerLicenseList?.length > 10 ? '500px' : '',
        }}
      >
        <DataGrid
          className="license-broker-list"
          rowHeight={36}
          sx={{
            border: 'none',
            '& .MuiDataGrid-main': {
              borderRadius: '3px',
              border: '1px solid #cccccc',
              width: '88%',
              paddingBottom: '10px',
              height: 'auto',
              display: 'block',
              alignItems: 'center' /* Center vertically */,
              justifyContent: 'center',
              margin: 'auto',
              overflowX: 'hidden',
              overflowY: 'auto',
            },
            '& .MuiDataGrid-menuIcon': {
              display: 'none', // Hide the menu icon
            },
            '& .MuiDataGrid-root': {
              borderColor: 'transparent',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .MuiDataGrid-root  .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-cell.Mui-selected': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'none !important',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .MuiDataGrid-cell:hover': {
              backgroundColor: 'none !important',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .Mui-selected': {
              backgroundColor: 'inherit', // Removes the selected background color for cells
            },
            '& MuiDataGrid-virtualScrollerContent': {
              maxHeight: '36px !important',
              minHeight: '36px !important',
            },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
              backgroundColor: '#fff !important',
              borderRadius: '3px',
              maxHeight: '36px !important',
              minHeight: '36px !important',

              fontSize: '14px',
              fontWeight: 500,
              marginTop: '0p',
              textAlign: 'center',
              marginBottom: '2px !important',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              borderTop: 'none',
              fontWeight: '500',
              padding: '10px 0',
              alignItems: 'center',
              justifyContent: 'left',
              '& .MuiTypography-root': {
                textWrap: 'auto',
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
              },
            },
            '& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]':
              {
                backgroundColor: '#cefcf6',
                fontSize: '14px',
                fontWeight: 800,
                marginBottom: '0px',
              },

            '& .MuiDataGrid-columnHeader': {
              border: 'none !important',
              height: '32px !important',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              cursor: 'default',
              outline: 'none !important',
              userSelect: 'none',
              padding: '10px 0',
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
              },
              '&:focus, &:focus-within': {
                outline: 'none !important',
              },
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              display: 'flex',
              alignItems: 'left' /* Center vertically */,
              justifyContent: 'left',
            },

            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '600', // Make header text bold
              fontSize: '12px',
              lineHeight: '15.88px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'hidden',
              overflowY: 'auto',
              margin: '0px',
            },
            '& .MuiDataGrid-columnSeparator--sideRight': {
              display: 'none', // Hide the right-side border separator between header cells
            },
            '& .MuiDataGrid-footerContainer': {
              display: 'none',
            },
            '& .MuiDataGrid-selectedRowCount': {
              display: 'none', // Hide the "1 row selected" text
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none', // Remove the default outline on focus
              background: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none', // Remove the default outline when focus is within the cell
            },
          }}
          rows={
            isLoading
              ? loadingData
              : brokerLicenseList?.length
              ? brokerLicenseList
              : noData
          }
          columns={columns}
          onSortModelChange={(model: GridSortModel) => {
            setSortModel(model);

            if (onSortChange && model.length > 0) {
              const sortItem = model[0];
              const fieldMap: Record<string, LicenseSortKey> = {
                licenseType: LicenseSortKey.Type,
                licenseNumber: LicenseSortKey.Number,
                expirationDate: LicenseSortKey.ExpirationDate,
                source: LicenseSortKey.CreatedAt,
                regionUuid: LicenseSortKey.Region,
                licenseHolder: LicenseSortKey.HolderFirstName,
              };

              const sortConfig = {
                key: fieldMap[sortItem.field] || LicenseSortKey.Type,
                order:
                  sortItem.sort === 'desc' ? SortOrder.Desc : SortOrder.Asc,
              };

              // Store sort in localStorage
              setStoredSort(sortConfig);

              onSortChange(sortConfig);
            } else if (onSortChange && model.length === 0) {
              // Reset to default sort when no sort is applied
              const defaultSort = {
                key: LicenseSortKey.CreatedAt,
                order: SortOrder.Desc,
              };
              setStoredSort(defaultSort);
              onSortChange(defaultSort);
            }
          }}
          sortingOrder={['asc', 'desc', null]}
          sortModel={sortModel}
        />
      </Box>
      <Stack
        className="add-License-container"
        data-testid="add-license-button"
        onClick={() => {
          if (brokerUuid !== undefined) {
            setAddNewLicense(true);
          } else {
            setIsBrokerSave(true);
          }
        }}
      >
        <AddCircleOutlineIcon className="add-circle-style" /> ADD LICENSE
      </Stack>
      {isBrokerSave && (
        <Stack>
          <LicenseModal
            title={'Save Before Adding Licenses'}
            onClose={() => {
              setIsBrokerSave(false);
            }}
            licenseSubmitHandler={() => {
              handleSubmitFromChild();
              setIsBrokerSave(false);
            }}
            submitLabel="Save Broker"
            context={
              'Please save your changes before adding licenses to this broker.'
            }
          />
        </Stack>
      )}
      {addNewLicense && (
        <Stack>
          <AddLicenseModal
            title={`${selectedLicense ? 'Update' : 'Add'} License`}
            selectedLicense={selectedLicense}
            setSelectedLicense={setSelectedLicense}
            associateBrokerageUuid={associateBrokerageUuid}
            brokerUuid={brokerUuid}
            brokerages={brokerage?.person?.edges || []}
            onClose={() => {
              setAddNewLicense(false);
              setSelectedLicense(null);
            }}
          />
        </Stack>
      )}
      {deleteModal && (
        <Stack>
          <DeleteLicenseModal
            DeleteHandler={() => {
              handleDelete();
            }}
            title={'Delete License Information'}
            onClose={() => {
              setDeleteModal(false);
            }}
            loading={loading}
            submitLabel="DELETE LICENSE"
            context={
              'Are you sure you want to delete this license information?'
            }
          />
        </Stack>
      )}
    </>
  );
};

export default LicenseList;
